// Helper murni untuk HALAMAN PEMBAYARAN nota (/orders/pay): parsing param
// multi-nota, ringkasan gabungan, preview alokasi saldo (cermin TS dari RPC
// pay_invoices_with_balance), dan utilitas WhatsApp/metode pembayaran.
//
// Sengaja BEBAS dari Supabase/React supaya gampang diuji (lihat
// src/lib/__tests__/payPage.test.ts). Data-layer ada di
// src/lib/supabase/invoices.ts; UI di src/components/PayClient.tsx.

import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
import { normalizePhone } from "@/lib/config";
import type { InvoiceStatus } from "@/lib/invoices";
import type { PaymentAccount } from "@/lib/paymentAccounts";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Batas jumlah nota dalam satu pembayaran (jaga URL & UI tetap waras). */
export const MAX_INVOICES_PER_PAYMENT = 20;

/** Parse `?inv=a,b,c` → daftar uuid valid, tanpa duplikat, maks 20. */
export function parseInvoiceIdsParam(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const id = part.trim().toLowerCase();
    if (!UUID_RE.test(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_INVOICES_PER_PAYMENT) break;
  }
  return out;
}

/**
 * Sisa mikrodetik SETELAH milidetik pada stempel ISO (`.123456` → 456).
 * `Date.parse` memotong di milidetik; Postgres menyimpan mikrodetik.
 */
function subMilliMicros(iso: string): number {
  const m = /\.(\d+)/.exec(iso);
  return m ? Number(m[1].padEnd(6, "0").slice(3)) : 0;
}

/**
 * Bandingkan dua stempel waktu ISO **sebagai waktu**, bukan sebagai teks.
 *
 * BUG YANG DITAMBAL (bughunt B5-6): urutan ini harus cocok dengan
 * `order by created_at` di server, dan `localeCompare` tidak bisa menjaminnya —
 * ia memakai kolasi ICU, dan di kolasi akar CLDR tanda baca `.` diurutkan SEBELUM
 * simbol `+`. Karena Postgres memangkas nol di belakang pecahan detik, stempel
 * berdetik-bulat diserialkan tanpa titik sama sekali (`…T10:00:00+00:00`) — dan
 * dibandingkan dengan saudara sedetiknya yang berpecahan (`…T10:00:00.5+00:00`),
 * `localeCompare` menaruhnya SESUDAHNYA, terbalik dari kronologis.
 *
 * Nilai yang tak terurai jatuh ke perbandingan string biasa: satu baris rusak
 * tidak boleh mengacak urutan seluruh daftar.
 */
export function compareIsoTime(a: string, b: string): number {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  if (ta !== tb) return ta - tb;
  return subMilliMicros(a) - subMilliMicros(b);
}

/** Urutan FIFO yang sama dengan server: created_at lalu invoice_no. */
export function sortInvoicesFifo<
  T extends { createdAt: string; invoiceNo: number }
>(list: ReadonlyArray<T>): T[] {
  return [...list].sort(
    (a, b) =>
      compareIsoTime(a.createdAt, b.createdAt) || a.invoiceNo - b.invoiceNo
  );
}

/**
 * Nota yang dibayar tombol "Bayar Semua" di /tagihan (pengganti "Pilih Semua",
 * permintaan pemilik 2026-09-02): seluruh nota siap-bayar yang TAMPIL, urut
 * FIFO (terlama dulu — cermin `pay_invoices_with_balance` dan urutan tampil
 * sub-kolom Belum Lunas), dipangkas MAX_INVOICES_PER_PAYMENT supaya URL
 * /orders/pay (`parseInvoiceIdsParam` memotong di angka yang sama) dan RPC
 * tetap waras. Sisa di luar batas dibayar pada pembayaran berikutnya.
 * Tidak memutasi input.
 */
export function pickInvoicesForPayAll<
  T extends { createdAt: string; invoiceNo: number }
>(list: ReadonlyArray<T>): T[] {
  return sortInvoicesFifo(list).slice(0, MAX_INVOICES_PER_PAYMENT);
}

/** Tautan halaman pembayaran untuk satu atau beberapa nota (`?inv=a,b,c`). */
export function payPageHref(invoiceIds: ReadonlyArray<string>): string {
  return `/orders/pay?inv=${invoiceIds.join(",")}`;
}

/** Jenis pembayaran pilihan lembar "Bayar Sekarang" (revisi tagihan DP). */
export type PayKind = "dp" | "pelunasan";

/**
 * Tautan halaman bayar SATU nota dengan nominal titipan lembar "Bayar
 * Sekarang" (`?nominal=`, `?jenis=`). Nominal hanya isi awal kolom Jumlah
 * Nominal — TIDAK dijepit ke sisa tagihan (PR 3, mig 7110): kelebihannya
 * masuk saldo pembeli setelah dikonfirmasi, dengan popup saat kirim bukti.
 */
export function payPageHrefWithNominal(
  invoiceId: string,
  nominal: number,
  kind: PayKind
): string {
  const n = Math.max(0, Math.round(nominal));
  return `${payPageHref([invoiceId])}&nominal=${n}&jenis=${kind}`;
}

/**
 * Nominal titipan `?nominal=` → bilangan bulat positif, atau null bila tak
 * ada/tak sah (huruf, negatif, nol, desimal, notasi ilmiah). Null = halaman
 * bayar memakai aturan lama (isi awal = sisa tagihan).
 */
export function parsePrefillNominal(raw: string | null | undefined): number | null {
  const s = (raw ?? "").trim();
  if (!/^\d{1,12}$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** `?jenis=` → jenis pembayaran yang dikenal, atau null. */
export function parsePayKind(raw: string | null | undefined): PayKind | null {
  return raw === "dp" || raw === "pelunasan" ? raw : null;
}

export type BatchSummary = {
  total: number;
  settled: number;
  outstanding: number;
  /** Jumlah nota yang masih bisa dibayar (open/partial & outstanding > 0). */
  payableCount: number;
};

/** Ringkasan gabungan beberapa nota untuk header halaman pembayaran. */
export function computeBatchSummary(
  invoices: ReadonlyArray<{
    status: InvoiceStatus;
    total: number;
    settled: number;
    outstanding: number;
  }>
): BatchSummary {
  let total = 0;
  let settled = 0;
  let outstanding = 0;
  let payableCount = 0;
  for (const inv of invoices) {
    total += inv.total;
    settled += inv.settled;
    outstanding += inv.outstanding;
    if (
      (inv.status === "open" || inv.status === "partial") &&
      inv.outstanding > 0
    ) {
      payableCount += 1;
    }
  }
  return { total, settled, outstanding, payableCount };
}

export type BalancePreviewRow = {
  invoiceId: string;
  invoiceNo: number;
  /** Kode nomor kustom (Penomoran Dokumen, mig 198) — null bila tak ada. */
  invoiceCode: string | null;
  applied: number;
  fullyPaid: boolean;
};

/**
 * Cermin TS dari pay_invoices_with_balance untuk PREVIEW UI (tidak menyentuh
 * server): pool = min(amount, balance) dialokasikan FIFO (createdAt,
 * invoiceNo) ke sisa tagihan tiap nota.
 */
export function previewBalanceAllocation(
  invoices: ReadonlyArray<{
    id: string;
    invoiceNo: number;
    invoiceCode?: string | null;
    createdAt: string;
    outstanding: number;
  }>,
  amount: number,
  balance: number
): { rows: BalancePreviewRow[]; applied: number; remainingOutstanding: number } {
  let pool = Math.max(0, Math.min(amount, balance));
  let applied = 0;
  let remainingOutstanding = 0;
  const rows: BalancePreviewRow[] = [];

  for (const inv of sortInvoicesFifo(invoices)) {
    const outstanding = Math.max(0, inv.outstanding);
    if (outstanding <= 0) continue;

    const pay = Math.min(pool, outstanding);
    pool -= pay;
    applied += pay;
    remainingOutstanding += outstanding - pay;
    rows.push({
      invoiceId: inv.id,
      invoiceNo: inv.invoiceNo,
      invoiceCode: inv.invoiceCode ?? null,
      applied: pay,
      fullyPaid: pay >= outstanding
    });
  }

  return { rows, applied, remainingOutstanding };
}

/**
 * Konversi nomor HP ke format wa.me (62xxx tanpa +). Menerima 08xx / +62xx /
 * 62xx (via normalizePhone); input lain → digit apa adanya (biar pemanggil
 * yang memutuskan fallback).
 */
export function phoneToWaNumber(raw: string | null | undefined): string {
  const local = normalizePhone(raw ?? "");
  if (/^08\d+$/.test(local)) return "62" + local.slice(1);
  return (raw ?? "").replace(/[^0-9]/g, "");
}

/**
 * Link chat WhatsApp berisi pesan siap kirim. Nomor kosong/tak valid → jatuh ke
 * `wa.me/?text=` (pengirim memilih kontak manual) supaya tombol tetap berguna.
 */
export function buildWaChatUrl(
  waNumber: string | null | undefined,
  message: string
): string {
  const num = (waNumber ?? "").replace(/[^0-9]/g, "");
  const text = encodeURIComponent(message);
  return num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
}

/**
 * Kontak admin handler pembeli — bentuk mentah RPC `get_my_admin_contact`.
 * Kedua field sengaja OPSIONAL: helper ini gerbang terakhir sebelum sebuah
 * nomor dipakai, jadi ia harus menerima bentuk apa pun yang bisa datang dari
 * jaringan (field hilang, bukan cuma null) tanpa memaksa pemanggil merapikannya.
 */
export type AdminContactInfo = { name?: string | null; phone?: string | null };

/** Tujuan tombol WhatsApp "hubungi admin" + nama yang BOLEH ikut di labelnya. */
export type AdminWaTarget = {
  /** Nomor tujuan wa.me (sudah dinormalkan). "" = tak ada nomor sama sekali. */
  waNumber: string;
  /**
   * Nama yang boleh dirender di label tombol. **null saat nomornya bukan milik
   * si handler** — lihat alasannya di `resolveAdminWaTarget`.
   */
  displayName: string | null;
  /** Asal nomor: WA pribadi handler, nomor WA toko, atau tak ada. */
  source: "handler" | "store" | "none";
};

/**
 * Satu pintu penentu "tombol WA ini membuka chat ke siapa, dan boleh menyebut
 * nama siapa".
 *
 * BUG YANG DITAMBAL (2026-08, zzhomey `/orders/tagihan/[id]`): nama dan nomor
 * dulu diresolusi dari DUA SUMBER TERPISAH lalu dirender berdampingan —
 * labelnya `contact.name` (admin handler dari `get_my_admin_contact`) sedangkan
 * nomornya `contact.phone || <WA toko>`. Begitu handler-nya tak punya
 * `profiles.phone`, labelnya tetap menyebut si handler tapi chat-nya terbuka ke
 * nomor WA toko — orang yang BERBEDA. Itu bukan kasus tepi: di PROD 2026-08-14
 * SELURUH admin zzhomey (ESA 286 pelanggan, Septi 269) ber-`phone` NULL, jadi
 * 100% pembelinya menekan "Kirim Link ke Admin (ESA)" lalu mendarat di chat
 * `085174411774`. Galea lolos hanya karena kedua handler-nya kebetulan ber-nomor.
 *
 * Aturannya sekarang STRUKTURAL, bukan sekadar tambalan di satu layar: nama
 * hanya ikut dipulangkan pada cabang yang nomornya memang milik dia
 * (`source: "handler"`), sehingga label yang menyebut orang yang tak kita hubungi
 * mustahil dirender. Fallback ke nomor toko tetap dipertahankan — tombol tak
 * boleh mati — hanya saja ia berhenti mengaku sebagai orang tertentu.
 *
 * Nomor tak valid (spasi/tanda hubung/teks) diperlakukan seperti kosong: handler
 * ber-nomor sampah jatuh ke nomor toko, dan toko ber-nomor sampah jatuh ke ""
 * yang oleh `buildWaChatUrl` dijadikan pemilih kontak manual.
 */
export function resolveAdminWaTarget(
  contact: AdminContactInfo | null | undefined,
  storeWhatsapp: string | null | undefined
): AdminWaTarget {
  const handlerNumber = phoneToWaNumber(contact?.phone);
  if (handlerNumber) {
    const name = (contact?.name ?? "").trim();
    return {
      waNumber: handlerNumber,
      displayName: name || null,
      source: "handler"
    };
  }

  const storeNumber = phoneToWaNumber(storeWhatsapp);
  if (storeNumber) {
    return { waNumber: storeNumber, displayName: null, source: "store" };
  }

  return { waNumber: "", displayName: null, source: "none" };
}

/**
 * Label tombol WA: `"Hubungi Admin"` → `"Hubungi Admin (Azra)"` HANYA bila
 * nomor tujuannya benar-benar milik Azra. Dipisah dari `resolveAdminWaTarget`
 * supaya tiap layar bebas memilih kata dasarnya sendiri ("Hubungi Admin",
 * "Kirim Link ke Admin") tanpa menyalin aturan boleh-tidaknya menyebut nama.
 */
export function adminWaLabel(base: string, target: AdminWaTarget): string {
  return target.displayName ? `${base} (${target.displayName})` : base;
}

/** Pesan WhatsApp konfirmasi transfer — menyebut nomor nota & nominal. */
export function buildPaymentWaMessage(
  input: {
    customerName: string;
    invoiceNos: number[];
    /**
     * Kode nomor kustom sejajar `invoiceNos` (Penomoran Dokumen, mig 198) —
     * dipakai menggantikan "#N" bila terisi.
     */
    invoiceCodes?: ReadonlyArray<string | null | undefined>;
    amountLabel: string;
    methodLabel?: string | null;
  },
  overrides?: WaTemplateOverrides | null
): string {
  const nos = input.invoiceNos
    .map((n, i) => {
      const code = (input.invoiceCodes?.[i] ?? "").trim();
      return code || `#${n}`;
    })
    .join(", ");

  return renderWaTemplate(resolveWaTemplate("bayar_konfirmasi", overrides), {
    "[nama pelanggan]": input.customerName.trim() || "pelanggan",
    "[nominal]": input.amountLabel,
    "[nomor nota]": nos,
    "[via metode]": input.methodLabel?.trim()
      ? ` via ${input.methodLabel.trim()}`
      : ""
  });
}

/**
 * Pesan WhatsApp untuk MENGIRIM LINK satu tagihan ke admin — dipakai tombol
 * "Kirim Link ke Admin" di halaman detail tagihan (/orders/tagihan/[id]).
 * Menyertakan judul nota + link agar admin gampang menelusuri tagihannya.
 */
export function buildInvoiceLinkWaMessage(
  input: {
    customerName: string;
    invoiceTitle: string;
    invoiceUrl: string;
    outstandingLabel?: string | null;
  },
  overrides?: WaTemplateOverrides | null
): string {
  const sisa = input.outstandingLabel?.trim();
  return renderWaTemplate(resolveWaTemplate("nota_tanya", overrides), {
    "[nama pelanggan]": input.customerName.trim() || "pelanggan",
    "[judul nota]": input.invoiceTitle,
    "[sisa tagihan]": sisa ? ` Sisa tagihan ${sisa}.` : "",
    "[link nota]": input.invoiceUrl
  });
}

/**
 * Link absolut ke halaman verifikasi top-up admin yang langsung menyorot satu
 * pengajuan (/admin/saldo?topup=<id>). Dipakai TopupClient untuk membuat
 * tautan "request ACC" di pesan WhatsApp pengajuan top-up.
 */
export function buildTopupReviewUrl(origin: string, requestId: string): string {
  const base = (origin ?? "").replace(/\/+$/, "");
  return `${base}/admin/saldo?topup=${requestId}`;
}

/**
 * Pesan WhatsApp pengajuan top-up saldo — dipakai TopupClient. Bila `reviewUrl`
 * diisi, link verifikasi (halaman Top-up Pending admin) ikut disertakan agar
 * admin bisa langsung membuka & meng-ACC pengajuan pelanggan itu.
 */
export function buildTopupWaMessage(
  input: {
    customerName: string;
    amountLabel: string;
    methodLabel?: string | null;
    reviewUrl?: string | null;
  },
  overrides?: WaTemplateOverrides | null
): string {
  const link = input.reviewUrl?.trim();
  return renderWaTemplate(resolveWaTemplate("topup", overrides), {
    "[nama pelanggan]": input.customerName.trim() || "pelanggan",
    "[nominal]": input.amountLabel,
    "[via metode]": input.methodLabel?.trim()
      ? ` via ${input.methodLabel.trim()}`
      : "",
    "[baris verifikasi]": link ? `Cek & verifikasi di sini: ${link}` : ""
  });
}

export type PayAmountCheck = {
  ok: boolean;
  /** Nominal ter-parse (dibulatkan, ≥ 0) — 0 bila input tak valid. */
  amount: number;
  error: string | null;
  /**
   * Hanya bila `allowExcess`: selisih di atas sisa tagihan (≥ 0) yang masuk
   * saldo pembeli saat setorannya dikonfirmasi (mig 7110).
   */
  excess?: number;
};

/**
 * Validasi kolom "Jumlah Nominal" pembayaran satu-metode (mig 178): pembeli
 * boleh membayar sebagian, default = sisa tagihan. Nominal harus > 0.
 * Tanpa `allowExcess` (Potong Saldo — saldo tak bisa berlebih) nominal tak
 * boleh melebihi sisa tagihan. Dengan `allowExcess` (transfer, revisi tagihan
 * DP PR 3 / jawaban pemilik #8) nominal di atas sisa BOLEH — selisihnya
 * `excess`, masuk saldo setelah dikonfirmasi admin; UI wajib menampilkan
 * popup peringatannya (`overpay.ts`).
 */
export function validatePayAmount(
  raw: string,
  outstanding: number,
  opts?: { allowExcess?: boolean }
): PayAmountCheck {
  const amount = Math.max(0, Math.round(Number(raw) || 0));
  if (amount <= 0) {
    return { ok: false, amount, error: "Nominal pembayaran harus lebih dari 0." };
  }
  const sisa = Math.max(0, outstanding);
  if (opts?.allowExcess) {
    return { ok: true, amount, error: null, excess: Math.max(0, amount - sisa) };
  }
  if (amount > sisa) {
    return { ok: false, amount, error: "Nominal melebihi sisa tagihan." };
  }
  return { ok: true, amount, error: null };
}

/**
 * Label metode pembayaran untuk tampilan — CERMIN snapshot method_label yang
 * dibangun server di submit_invoice_payment_batch (mig 077).
 */
export function buildAccountMethodLabel(
  acc: Pick<PaymentAccount, "type" | "name" | "accountNumber" | "accountHolder">
): string {
  if (acc.type === "qris") return `QRIS — ${acc.name}`;
  const number = acc.accountNumber?.trim();
  const holder = acc.accountHolder?.trim();
  return [acc.name, number || null, holder ? `a.n. ${holder}` : null]
    .filter(Boolean)
    .join(" ");
}

// ─── Pembayaran KOMBINASI (split payment, mig 166) ───────────────────────────
// Satu nota dibayar beberapa metode sekaligus (mis. BCA Rp50.000 + QRIS
// Rp5.000). Helper murni di bawah dipakai PayClient (pembeli) & ManualPaymentPanel
// (admin) untuk validasi & ringkasan; rincian disimpan di
// invoice_payments.splits.

/** Rincian satu porsi setoran kombinasi (cermin invoice_payments.splits). */
export type ComboSplit = {
  methodLabel: string | null;
  paymentAccountId: string | null;
  amount: number;
  proofUrl: string | null;
};

/** Total semua porsi kombinasi. */
export function comboSplitsTotal(
  splits: ReadonlyArray<{ amount: number }>
): number {
  return splits.reduce((s, p) => s + Math.max(0, p.amount || 0), 0);
}

/**
 * Ringkasan rincian kombinasi untuk tampilan, mis.
 * "BCA Rp50.000 + QRIS Rp5.000". Porsi tanpa label memakai "Metode".
 */
export function summarizeComboSplits(
  splits: ReadonlyArray<{ methodLabel: string | null; amount: number }>
): string {
  return splits
    .map(
      (p) =>
        `${p.methodLabel?.trim() || "Metode"} Rp ${Math.max(
          0,
          Math.round(p.amount || 0)
        ).toLocaleString("id-ID")}`
    )
    .join(" + ");
}

/** Satu baris draft kombinasi di UI (sebelum dikirim ke server). */
export type ComboPortionDraft = {
  /** id rekening payment_accounts (pembeli) atau "cash"/id (admin). */
  methodKey: string;
  /** Nominal mentah dari input. */
  amountRaw: string;
};

export type ComboValidation = {
  ok: boolean;
  total: number;
  error: string | null;
  /** Hanya bila `allowExcess`: selisih di atas sisa tagihan (mig 7110). */
  excess?: number;
};

/**
 * Validasi draft kombinasi (pembeli & admin). `requireMethod` true → tiap porsi
 * wajib memilih metode (pembeli pilih rekening). Tanpa `allowExcess` total tak
 * boleh melebihi sisa tagihan; dengan `allowExcess` (revisi tagihan DP PR 3)
 * total di atas sisa boleh — selisihnya `excess`, masuk saldo pembeli saat
 * dikonfirmasi (UI menampilkan popup peringatan).
 */
export function validateComboPortions(
  rows: ReadonlyArray<ComboPortionDraft>,
  outstanding: number,
  opts?: { requireMethod?: boolean; allowExcess?: boolean }
): ComboValidation {
  const requireMethod = opts?.requireMethod ?? true;
  let total = 0;
  if (rows.length === 0) {
    return { ok: false, total: 0, error: "Tambahkan minimal satu metode." };
  }
  for (const r of rows) {
    const amount = Math.round(Number(r.amountRaw) || 0);
    if (requireMethod && !r.methodKey.trim()) {
      return { ok: false, total, error: "Pilih metode untuk tiap porsi." };
    }
    if (amount <= 0) {
      return { ok: false, total, error: "Nominal tiap metode harus lebih dari 0." };
    }
    total += amount;
  }
  if (opts?.allowExcess) {
    return { ok: true, total, error: null, excess: Math.max(0, total - Math.max(0, outstanding)) };
  }
  if (total > outstanding) {
    return {
      ok: false,
      total,
      error: "Total kombinasi melebihi sisa tagihan."
    };
  }
  return { ok: true, total, error: null };
}
